import { useState, useEffect } from 'react';
import { createUserAccount } from '../../../firebase/auth';
import { createSalonProfile } from '../../../firebase/salon';
import { getAuth, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase/firebase';
import { addUserToSalon } from '../../../firebase/userRoles';

// Chave para armazenamento no localStorage
const SETUP_STORAGE_KEY = 'bookly_setup_data';

// Chave para armazenamento da senha no sessionStorage (mais seguro que localStorage)
const SETUP_PASSWORD_KEY = 'bookly_setup_password';

// Chave para armazenamento temporário de serviços
const SERVICES_TEMP_STORAGE_KEY = 'bookly_services_temp';

/**
 * Hook para gerenciar o estado e a lógica do fluxo de onboarding
 * Com correção para lidar melhor com dados sensíveis
 */
const useOnboardingFlow = (navigate) => {
  console.log('[DEBUG-ONBOARDING] Inicializando hook useOnboardingFlow');
  
  // Recuperar dados do localStorage/sessionStorage imediatamente ao inicializar o hook
  // Isso previne possíveis problemas de temporização com useEffect
  const getInitialState = () => {
    try {
      const savedSetupData = localStorage.getItem(SETUP_STORAGE_KEY);
      const savedPassword = sessionStorage.getItem(SETUP_PASSWORD_KEY);
      
      console.log('[INIT-LOAD] Tentando carregar dados iniciais:', {
        temLocalStorage: !!savedSetupData,
        temSessionStorage: !!savedPassword
      });
      
      // Verificar se há dados temporários de serviços
      const tempServicesData = localStorage.getItem(SERVICES_TEMP_STORAGE_KEY);
      
      if (savedSetupData) {
        const parsedData = JSON.parse(savedSetupData);
        
        if (parsedData && parsedData.setupData && parsedData.lastStep) {
          console.log('[INIT-LOAD] Dados encontrados para o passo:', parsedData.lastStep);
          
          // Verificar se temos dados temporários de serviços e mesclar se necessário
          if (tempServicesData && (!parsedData.setupData.categories || parsedData.setupData.categories.length === 0)) {
            try {
              console.log('[INIT-LOAD] Encontrados dados de serviços temporários, tentando mesclar');
              const parsedServices = JSON.parse(tempServicesData);
              if (Array.isArray(parsedServices) && parsedServices.length > 0) {
                console.log('[INIT-LOAD] Mesclando dados de serviços temporários', parsedServices.length);
                parsedData.setupData.categories = parsedServices;
              }
            } catch (e) {
              console.error('[INIT-LOAD] Erro ao processar dados temporários de serviços:', e);
            }
          }
          
          // Restaurar senha se disponível
          if (parsedData.setupData.password === '********') {
            if (savedPassword) {
              parsedData.setupData.password = savedPassword;
            } else {
              // Tentar obter do backup de emergência
              try {
                const credentialsBackup = sessionStorage.getItem('signup_credentials_backup');
                if (credentialsBackup) {
                  const credentials = JSON.parse(credentialsBackup);
                  if (credentials && credentials.password) {
                    parsedData.setupData.password = credentials.password;
                  }
                }
              } catch (e) {
                console.error('[INIT-LOAD] Erro ao recuperar backup de senha:', e);
              }
            }
          }
          
          return {
            initialStep: parsedData.lastStep,
            initialData: parsedData.setupData
          };
        }
      } else if (tempServicesData) {
        // Se não temos dados principais, mas temos dados temporários de serviços
        try {
          console.log('[INIT-LOAD] Apenas dados temporários encontrados, criando estado inicial');
          const parsedServices = JSON.parse(tempServicesData);
          if (Array.isArray(parsedServices) && parsedServices.length > 0) {
            // Criar um estado inicial com os serviços temporários
            return {
              initialStep: 5, // Etapa de serviços
              initialData: {
                categories: parsedServices,
                // Outros campos vazios
                name: '',
                businessName: '',
                phone: '',
                email: '',
                password: '',
                businessSize: '',
                businessType: '',
                businessTypeName: '',
                address: {},
                businessHours: {},
                professionals: []
              }
            };
          }
        } catch (e) {
          console.error('[INIT-LOAD] Erro ao processar apenas dados temporários:', e);
        }
      }
    } catch (e) {
      console.error('[INIT-LOAD] Erro ao carregar dados iniciais:', e);
    }
    
    return { initialStep: 1, initialData: null };
  };
  
  // Carregar estado inicial no momento da criação do hook
  const { initialStep, initialData } = getInitialState();
  
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [error, setError] = useState(null);
  const [completedSalonId, setCompletedSalonId] = useState(null);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [accountData, setAccountData] = useState(() => {
    // Usar dados recuperados ou valores padrão
    return initialData || {
      // Dados pessoais e da conta
      name: '',
      businessName: '',
      phone: '',
      email: '',
      password: '',
      
      // Configurações do negócio
      businessSize: '',
      businessType: '',
      businessTypeName: '',
      address: {},
      categories: [],
      businessHours: {},
      professionals: []
    };
  });
  
  // Verificar se estamos na URL correta para o fluxo de cadastro
  useEffect(() => {
    const currentPath = window.location.pathname;
    // Garantir que estejamos na rota /signup para qualquer etapa do fluxo
    if (currentPath !== '/signup') {
      console.log('[DEBUG-RELOAD] Detectada URL incorreta, redirecionando para /signup');
      // Usar navigate do React Router para preservar o estado
      navigate('/signup', { replace: true });
    }
  }, [navigate, window.location.pathname]);
  
  // Verificação de diagnóstico no momento da montagem e quando dados importantes mudam
  useEffect(() => {
    console.log('[DEBUG-RELOAD] Verificação periódica do estado:', {
      currentStep,
      temDadosSalvosLocalStorage: !!localStorage.getItem(SETUP_STORAGE_KEY),
      temSenhaSalvaSessionStorage: !!sessionStorage.getItem(SETUP_PASSWORD_KEY),
      temSenhaNoAccountData: !!accountData.password && accountData.password !== '********',
      urlAtual: window.location.pathname
    });
    
    // CORREÇÃO CRÍTICA: Se temos o passo atual > 1, mas não temos dados válidos, pode haver um problema
    if (currentStep > 1 && (!accountData.email || !accountData.password || accountData.password === '********')) {
      console.warn('[DEBUG-RELOAD] Estado inconsistente detectado - tentando recuperar novamente');
      const { initialStep, initialData } = getInitialState();
      if (initialData && initialData.email) {
        // Só atualizar se realmente tivermos dados melhores
        setAccountData(initialData);
        if (initialStep !== currentStep) {
          setCurrentStep(initialStep);
        }
      }
    }
  }, [currentStep, accountData, window.location.pathname]);
  
  // Verificar o conteúdo do localStorage/sessionStorage no carregamento inicial
  console.log('[DEBUG-RELOAD] Estado do armazenamento na inicialização do hook:', {
    temLocalStorage: !!localStorage.getItem(SETUP_STORAGE_KEY),
    temSessionStorage: !!sessionStorage.getItem(SETUP_PASSWORD_KEY),
    urlAtual: window.location.href,
    timestamp: new Date().toISOString()
  });
  
  // FUNÇÃO AUXILIAR: Salvar dados com garantia de integridade
  const saveDataToStorage = (step, data) => {
    try {
      // 1. Salvar senha no sessionStorage se ela existir
      if (data.password && data.password !== '********') {
        sessionStorage.setItem(SETUP_PASSWORD_KEY, data.password);
      }
      
      // 2. Preparar dados para localStorage (sem senha direta)
      const dataToSave = {
        lastStep: step,
        setupData: {
          ...data,
          // Mascarar senha por segurança
          password: data.password ? '********' : ''
        }
      };
      
      // 3. Salvar no localStorage
      localStorage.setItem(SETUP_STORAGE_KEY, JSON.stringify(dataToSave));
      
      // 4. Verificar se foi salvo corretamente
      const verificacao = localStorage.getItem(SETUP_STORAGE_KEY);
      return !!verificacao;
    } catch (e) {
      console.error('[STORAGE] Erro ao salvar dados:', e);
      return false;
    }
  };
  
  // Salvar dados no localStorage sempre que houver mudanças
  useEffect(() => {
    // Evitar salvar em situações desnecessárias
    if (currentStep === totalSteps || loadingSubmit) {
      return; // Não salvar durante loading ou na tela final
    }
    
    // Não salvar se não temos dados significativos
    if (!accountData.email && currentStep === 1) {
      return; // Evitar salvar dados vazios no primeiro passo
    }
    
    console.log(`[DEBUG-ONBOARDING] Salvando progresso do passo ${currentStep}`);
    
    // Verificar problemas potenciais com serialização
    const problemasSerializacao = [];
    
    // Verificar dados complexos que podem causar problemas
    if (accountData.professionals && accountData.professionals.length > 0) {
      accountData.professionals.forEach((prof, idx) => {
        if (prof.photo instanceof File) {
          problemasSerializacao.push(`professional[${idx}].photo é um objeto File não serializável`);
        }
      });
    }
    
    if (problemasSerializacao.length > 0) {
      console.warn('[DEBUG-ONBOARDING] Problemas potenciais de serialização encontrados:', problemasSerializacao);
    }
    
    // Salvar dados usando a função auxiliar
    const saveSuccess = saveDataToStorage(currentStep, accountData);
    
    if (saveSuccess) {
      console.log(`[DEBUG-ONBOARDING] Dados salvos com sucesso no passo ${currentStep}`);
    } else {
      console.error('[DEBUG-ONBOARDING] Falha ao salvar dados');
    }
  }, [currentStep, accountData, loadingSubmit]);
  
  // Total de etapas no fluxo (incluindo tela de sucesso)
  const totalSteps = 8;
  
  // Progresso atual (porcentagem)
  const progress = Math.round((currentStep / (totalSteps - 1)) * 100);
  
  // Navegação entre etapas
  const goToNextStep = (data) => {
    // Se estamos no penúltimo passo (7), precisamos enviar os dados antes de ir para o último passo
    const isLastStep = currentStep === 7;
    
    // Primeiro atualizar os dados
    setAccountData(prevData => {
      const newData = {
        ...prevData,
        ...data
      };
      
      // Se for o último passo, agendar o handleSubmit para ser executado após a atualização do estado
      if (isLastStep) {
        console.log("[DEBUG-ONBOARDING] Último passo, agendando handleSubmit");
        // Usar setTimeout para garantir que o estado foi atualizado
        setTimeout(() => {
          handleSubmit(newData);
        }, 0);
      }
      
      // IMPORTANTE: Tentar salvar os dados imediatamente para evitar problemas de timing
      saveDataToStorage(isLastStep ? currentStep : currentStep + 1, newData);
      
      return newData;
    });
    
    if (currentStep < totalSteps && !isLastStep) {
      // Avançar para o próximo passo
      console.log(`[DEBUG-ONBOARDING] Avançando de passo ${currentStep} para ${currentStep + 1}`);
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  
  const goToPreviousStep = () => {
    if (currentStep > 1) {
      console.log(`[DEBUG-ONBOARDING] Retornando de passo ${currentStep} para ${currentStep - 1}`);
      setCurrentStep(currentStep - 1);
      
      // Também salvar o novo estado do passo imediatamente
      saveDataToStorage(currentStep - 1, accountData);
      
      window.scrollTo(0, 0);
    }
  };
  
  // Limpar dados salvos e reiniciar o processo
  const clearSavedData = () => {
    console.log('[DEBUG-ONBOARDING] Limpando todos os dados salvos e reiniciando processo');
    localStorage.removeItem(SETUP_STORAGE_KEY);
    sessionStorage.removeItem(SETUP_PASSWORD_KEY);
    sessionStorage.removeItem('signup_credentials_backup');
    setAccountData({
      name: '',
      businessName: '',
      phone: '',
      email: '',
      password: '',
      businessSize: '',
      businessType: '',
      businessTypeName: '',
      address: {},
      categories: [],
      businessHours: {},
      professionals: []
    });
    setCurrentStep(1);
    setError(null);
    window.scrollTo(0, 0);
  };
  
  // Enviar email de verificação para o usuário
  const sendVerificationEmail = async (user) => {
    console.log('[DEBUG-ONBOARDING] Tentando enviar email de verificação');
    try {
      await sendEmailVerification(user);
      setVerificationSent(true);
      console.log('[DEBUG-ONBOARDING] Email de verificação enviado com sucesso');
      return true;
    } catch (error) {
      console.error('[DEBUG-ONBOARDING] Erro ao enviar email de verificação:', error);
      return false;
    }
  };
  
  // Verificar se o email do usuário foi verificado
  const checkEmailVerification = async (user) => {
    console.log('[DEBUG-ONBOARDING] Verificando status de verificação de email');
    try {
      // Recarregar o usuário para obter status atualizado
      await user.reload();
      const isVerified = user.emailVerified;
      setIsEmailVerified(isVerified);
      console.log('[DEBUG-ONBOARDING] Status de verificação:', isVerified ? 'Verificado' : 'Não verificado');
      return isVerified;
    } catch (error) {
      console.error('[DEBUG-ONBOARDING] Erro ao verificar email:', error);
      return false;
    }
  };
  
  // Submeter o formulário e criar conta
  const handleSubmit = async (dataToSubmit) => {
    console.log('[DEBUG-ONBOARDING] Iniciando submissão do formulário final');
    try {
      setLoadingSubmit(true);
      setError(null);
      
      // Usar os dados fornecidos ou o estado atual (para garantir que temos os dados mais recentes)
      const data = dataToSubmit || accountData;
      
      // Verificar se precisamos recuperar a senha do sessionStorage (caso esteja mascarada)
      if (data.password === '********') {
        console.log('[DEBUG-ONBOARDING] Detectada senha mascarada, tentando recuperar do sessionStorage');
        const savedPassword = sessionStorage.getItem(SETUP_PASSWORD_KEY);
        if (savedPassword) {
          console.log('[DEBUG-ONBOARDING] Senha recuperada do sessionStorage');
          data.password = savedPassword;
        } else {
          console.error('[DEBUG-ONBOARDING] Erro crítico: Senha mascarada e não encontrada no sessionStorage');
          throw new Error('A senha foi perdida durante o processo. Por favor, reinicie o cadastro.');
        }
      }
      
      console.log("[DEBUG-ONBOARDING] Dados para cadastro:", {
        email: data.email,
        temSenha: !!data.password,
        senhaMascarada: data.password === '********',
        name: data.name,
        businessName: data.businessName,
        professionalCount: Array.isArray(data.professionals) ? data.professionals.length : 'N/A'
      });
      
      // PROBLEMA CRÍTICO: verificar se a senha está mascarada
      if (!data.password || data.password === '********') {
        console.error('[DEBUG-ONBOARDING] ERRO CRÍTICO: Senha mascarada ou ausente no momento do cadastro');
        throw new Error('A senha foi perdida durante o processo. Por favor, reinicie o cadastro.');
      }
      
      // Criar conta de usuário
      console.log('[DEBUG-ONBOARDING] Tentando criar conta de usuário');
      const userCredential = await createUserAccount(
        data.email,
        data.password,
        data.name
      );
      
      if (!userCredential || !userCredential.user) {
        console.error('[DEBUG-ONBOARDING] Falha ao criar usuário, resposta vazia');
        throw new Error('Não foi possível criar a conta. Tente novamente.');
      }
      
      console.log('[DEBUG-ONBOARDING] Conta de usuário criada com sucesso, UID:', userCredential.user.uid);
      
      // Obter ID do usuário
      const userId = userCredential.user.uid;
      
      // Criar documento na coleção users conforme modelo de dados em contas.md
      await setDoc(doc(db, 'users', userId), {
        email: data.email,
        displayName: data.name,
        phoneNumber: data.phone || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isSuperAdmin: false
      });
      
      // Enviar email de verificação
      await sendVerificationEmail(userCredential.user);
      
      // Garantir que as categorias estejam no formato correto
      const formattedCategories = data.categories.map(category => ({
        name: category.name,
        services: category.services.map(service => ({
          name: service.name,
          price: service.price,
          duration: service.duration,
          description: service.description || ''
        }))
      }));
      
      // Garantir que os profissionais estejam no formato correto
      const formattedProfessionals = Array.isArray(data.professionals) 
        ? data.professionals.map(prof => {
            // Remover a URL de pré-visualização da foto e outros dados temporários
            const { photoURL, photo, ...profData } = prof;
            return profData;
          })
        : [];
      
      console.log("[useOnboardingFlow] Profissionais formatados para envio:", {
        count: formattedProfessionals.length,
        data: formattedProfessionals
      });
      
      // Formatar os horários de funcionamento para garantir dados limpos
      const formattedBusinessHours = {};
      Object.keys(data.businessHours).forEach(day => {
        // Garantir que apenas dias com dados válidos sejam incluídos
        if (data.businessHours[day] && data.businessHours[day].isOpen) {
          // Filtrar intervalos vazios ou inválidos
          const validIntervals = data.businessHours[day].intervals.filter(
            interval => interval.start && interval.end && interval.start !== interval.end
          );
          
          if (validIntervals.length > 0) {
            formattedBusinessHours[day] = {
              isOpen: true,
              intervals: validIntervals
            };
          } else {
            formattedBusinessHours[day] = {
              isOpen: false,
              intervals: []
            };
          }
        } else {
          formattedBusinessHours[day] = {
            isOpen: false,
            intervals: []
          };
        }
      });
      
      // Criar perfil do salão
      const salonProfile = {
        name: data.businessName,
        owner: {
          uid: userId,
          name: data.name,
          email: data.email,
          phone: data.phone,
          emailVerified: false // Inicialmente, o email não está verificado
        },
        businessSize: data.businessSize,
        businessType: data.businessType,
        businessTypeName: data.businessTypeName,
        address: data.address,
        categories: formattedCategories,
        businessHours: formattedBusinessHours,
        professionals: formattedProfessionals,
        createdAt: new Date()
      };
      
      console.log("[useOnboardingFlow] Enviando salonProfile para createSalonProfile:", {
        ...Object.keys(salonProfile).reduce((acc, key) => {
          if (key === 'professionals') {
            acc[key] = { 
              count: salonProfile[key].length,
              sample: salonProfile[key].length > 0 ? salonProfile[key][0] : null 
            };
          } else {
            acc[key] = salonProfile[key];
          }
          return acc;
        }, {})
      });
      
      const salonId = await createSalonProfile(salonProfile);
      
      // Adicionar usuário ao salão com a role de proprietário (owner)
      // Usando a função existente addUserToSalon do modelo de dados de contas.md
      await addUserToSalon(userId, salonId, 'owner');
      
      // Processar profissionais adicionados durante o setup
      if (formattedProfessionals.length > 0) {
        console.log("[useOnboardingFlow] Processando convites para profissionais:", formattedProfessionals.length);
        
        // Importar função necessária para criar convites
        const { createUserInvitation } = await import('../../../firebase/invitations');
        
        // Para cada profissional que tem email, criar um convite
        for (const prof of formattedProfessionals) {
          if (prof.email) {
            try {
              // Buscar o ID do profissional no Firestore
              // Esta é uma simplificação; na implementação real você precisaria
              // buscar o ID correto do profissional baseado em critérios mais robustos
              const professionalId = prof.id; // ou alguma lógica para determinar o ID

              // Criar convite para o profissional
              await createUserInvitation(
                salonId,
                prof.email,
                'professional',
                [], // permissões vazias para profissionais
                {
                  nome: prof.name,
                  email: prof.email,
                  telefone: prof.phone
                },
                userId // ID do usuário atual como convidante
              );
              
              console.log(`[useOnboardingFlow] Convite criado para ${prof.name} (${prof.email})`);
            } catch (error) {
              console.error(`[useOnboardingFlow] Erro ao criar convite para ${prof.email}:`, error);
              // Não interromper o fluxo principal se houver erro nos convites
            }
          }
        }
      }
      
      console.log("[useOnboardingFlow] Estrutura de usuário e permissões criadas com sucesso:", {
        userId, 
        salonId,
        role: 'owner'
      });
      
      // Armazenar o ID do salão para exibir na tela de sucesso
      setCompletedSalonId(salonId);
      
      // Limpar dados salvos após conclusão bem-sucedida
      console.log('[DEBUG-ONBOARDING] Limpando dados do localStorage e sessionStorage após conclusão bem-sucedida');
      localStorage.removeItem(SETUP_STORAGE_KEY);
      sessionStorage.removeItem(SETUP_PASSWORD_KEY);
      
      // Avançar para a tela de sucesso
      console.log('[DEBUG-ONBOARDING] Avançando para tela de sucesso (passo final)');
      setCurrentStep(totalSteps);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error('[DEBUG-ONBOARDING] Erro ao criar conta:', err);
      setError(err.message);
    } finally {
      setLoadingSubmit(false);
    }
  };
  
  return {
    currentStep,
    totalSteps,
    progress,
    accountData,
    loadingSubmit,
    error,
    completedSalonId,
    verificationSent,
    isEmailVerified,
    goToNextStep,
    goToPreviousStep,
    clearSavedData,
    sendVerificationEmail,
    checkEmailVerification,
    stepTitles: [
      'Dados pessoais',
      'Tamanho do negócio',
      'Segmento',
      'Endereço',
      'Serviços',
      'Horários',
      'Profissionais',
      'Concluído'
    ]
  };
};

export default useOnboardingFlow; 